import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Loading from '../components/Loading';
import ExpenseForm from '../components/ExpenseForm';
import Attachments from '../components/Attachments';

const EmployeeExpenseDashboard = () => {
  const { user } = useAuth();
  const toast = useToast();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payTotals, setPayTotals] = useState(null);

  const loadSubmissions = useCallback(async () => {
    try {
      const res = await axios.get('/api/employee-expenses/mine');
      setSubmissions(res.data);
    } catch (e) {
      toast.error('Failed to load your submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const loadPayments = useCallback(async () => {
    try {
      const res = await axios.get('/api/payments/mine');
      setPayments(res.data.payments || []);
      setPayTotals(res.data.totals || null);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const stats = useMemo(() => {
    const s = { total: submissions.length, pending: 0, approved: 0, rejected: 0, balance: 0 };
    submissions.forEach((e) => {
      if (s[e.status] !== undefined) s[e.status] += 1;
      s.balance += Number(e.balanceAmount) || 0;
    });
    return s;
  }, [submissions]);

  const formatCurrency = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(v) || 0);

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const statusBadge = (s) => {
    const map = {
      pending: { background: '#fef3c7', color: '#92400e' },
      approved: { background: '#d1fae5', color: '#065f46' },
      rejected: { background: '#fee2e2', color: '#991b1b' },
    };
    const st = map[s] || map.pending;
    return <span className="badge" style={st}>{s}</span>;
  };

  if (loading) return <Loading />;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Employee Expense Dashboard</h1>
          <p className="page-subtitle">
            Welcome, {user?.name} — Employee ID: {user?.employeeId || user?.email}
          </p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Submitted</div>
            <div className="stat-value">{stats.total}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon yellow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Pending</div>
            <div className="stat-value">{stats.pending}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Approved</div>
            <div className="stat-value">{stats.approved}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon red">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Rejected</div>
            <div className="stat-value">{stats.rejected}</div>
          </div>
        </div>
      </div>

      <div className="stats-grid" style={{ marginTop: '16px' }}>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">Total Payable</div>
            <div className="stat-value">{formatCurrency(payTotals?.totalPayable)}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">Total Paid by Admin</div>
            <div className="stat-value">{formatCurrency(payTotals?.totalPaidByAdmin ?? payTotals?.totalPaid)}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">Advance Paid</div>
            <div className="stat-value">{formatCurrency(payTotals?.advancePaid)}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">Remaining Payable Balance</div>
            <div className="stat-value">{formatCurrency(payTotals?.remainingPayableBalance ?? payTotals?.remaining)}</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '16px' }}>
        <div className="card-body">
          <h3 className="modal-title" style={{ marginBottom: '16px' }}>Payments Received</h3>
          {payments.length === 0 ? (
            <div className="empty-state">
              <h3 className="empty-state-title">No payments yet</h3>
              <p className="empty-state-text">Payments made to you will appear here</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Payment Mode</th>
                    <th>Status</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p._id}>
                      <td>{formatDate(p.date)}</td>
                      <td>
                        {p.type === 'advance'
                          ? <span className="badge" style={{ background: '#e0e7ff', color: '#3730a3' }}>Advance</span>
                          : <span className="badge" style={{ background: '#d1fae5', color: '#065f46' }}>Payable</span>}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(p.amount)}</td>
                      <td>{p.paymentMode || '—'}</td>
                      <td>
                        {p.status === 'Settled'
                          ? <span className="badge" style={{ background: '#d1fae5', color: '#065f46' }}>Settled</span>
                          : p.status === 'Partial'
                            ? <span className="badge" style={{ background: '#fef3c7', color: '#92400e' }}>Partial</span>
                            : <span className="badge" style={{ background: '#e5e7eb', color: '#374151' }}>{p.status || 'Pending'}</span>}
                      </td>
                      <td>{p.remarks || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ maxWidth: '800px' }}>
        <div className="card-body">
          <h3 className="modal-title" style={{ marginBottom: '16px' }}>Submit New Expense</h3>
          <ExpenseForm onSubmitted={loadSubmissions} />
        </div>
      </div>

      <div className="card" style={{ marginTop: '16px' }}>
        <div className="card-body">
          <h3 className="modal-title" style={{ marginBottom: '16px' }}>My Submitted Expenses</h3>
          {submissions.length === 0 ? (
            <div className="empty-state">
              <h3 className="empty-state-title">No submissions yet</h3>
              <p className="empty-state-text">Your submitted expenses will appear here</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Expense ID</th>
                    <th>Date</th>
                    <th>Nature</th>
                    <th>Contractor</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((e) => (
                    <tr key={e._id}>
                      <td style={{ fontWeight: 600 }}>{e.expenseId}</td>
                      <td>{formatDate(e.date)}</td>
                      <td>{e.expenseNature}</td>
                      <td>{e.contractorName}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(e.amount)}</td>
                      <td style={{ textAlign: 'right', color: Number(e.balanceAmount) > 0 ? 'var(--danger)' : 'var(--success)' }}>{formatCurrency(e.balanceAmount)}</td>
                      <td>{statusBadge(e.status)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => setSelected(e)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Expense {selected.expenseId}</h3>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.9rem' }}>
                <div><strong>Date:</strong> {formatDate(selected.date)}</div>
                <div><strong>Status:</strong> {statusBadge(selected.status)}</div>
                <div><strong>Nature:</strong> {selected.expenseNature}</div>
                <div><strong>Contractor:</strong> {selected.contractorName}</div>
                <div><strong>Project:</strong> {selected.projectName || '—'}</div>
                <div><strong>Bill No:</strong> {selected.billNumber || '—'}</div>
                <div><strong>Payment Mode:</strong> {selected.paymentMode}</div>
                <div><strong>Paid By:</strong> {selected.paidBy || '—'}</div>
                <div><strong>Amount:</strong> {formatCurrency(selected.amount)}</div>
                <div><strong>Paid:</strong> {formatCurrency(selected.paidAmount)}</div>
                <div><strong>Balance:</strong> {formatCurrency(selected.balanceAmount)}</div>
              </div>
              {selected.remarks && (
                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label className="form-label">Remarks</label>
                  <div style={{ fontSize: '0.9rem' }}>{selected.remarks}</div>
                </div>
              )}
              {selected.adminRemark && (
                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label className="form-label">Admin Remark</label>
                  <div className="alert" style={{ fontSize: '0.9rem' }}>{selected.adminRemark}</div>
                </div>
              )}
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label className="form-label">Attachments</label>
                <Attachments attachments={selected.attachments} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeExpenseDashboard;